import { useEffect, useState } from 'react';

/**
 * How long a running timer has been going, counted in the browser and ticking every second.
 *
 * IT IS NOT `elapsedMinutes` FROM THE API, and that distinction is the whole reason this
 * exists. The server computes that figure when it answers, so it is correct exactly once:
 * re-rendering afterwards redraws the same number. A timer showing "0m" that never moves
 * looks broken, which is worse than showing nothing at all — you cannot tell a stopped clock
 * from a running one.
 *
 * `startedAt` is the only thing the server needs to send. Counting from a fixed instant means
 * the display cannot drift no matter how long a tab sits open or asleep, and any two places
 * showing the same timer agree because they are counting from the same instant rather than
 * each keeping their own tally.
 */
export function useElapsed(startedAt: string | null | undefined): number {
  const [seconds, setSeconds] = useState(() => secondsSince(startedAt));

  useEffect(() => {
    if (!startedAt) return;
    setSeconds(secondsSince(startedAt));
    const id = window.setInterval(() => setSeconds(secondsSince(startedAt)), 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  return seconds;
}

function secondsSince(startedAt: string | null | undefined): number {
  if (!startedAt) return 0;
  const from = Date.parse(startedAt);
  if (Number.isNaN(from)) return 0;
  return Math.max(0, Math.floor((Date.now() - from) / 1000));
}

/**
 * `0:07`, `12:34`, `1:02:03`.
 *
 * SECONDS ARE SHOWN, which `formatMinutes` deliberately does not do — a booked figure of
 * "2h 15m" is what somebody is billed for and seconds would be false precision. A running
 * clock is the opposite: the seconds are the only part that visibly moves, and movement is
 * the whole message.
 */
export function formatElapsed(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
